/**
 * FL-246: ドライバー稼働スケジュール デモデータ
 *
 * 陸送員の週間稼働スケジュール管理機能用のデモデータ
 * 2025年10月28日（火曜日）を基準として設定
 *
 * デモシナリオ:
 * - 複数陸送員の週間スケジュール
 * - 様々な勤務時間パターン
 * - 異なるステータス（registered, confirmed, cancelled）
 * - 一括複写元フラグのテスト用パターン
 */

import { DriverSchedule } from '../../types/schedule';

/**
 * サンプル陸送員ID（caseData.tsと一致）
 */
const SAMPLE_TRANSPORTERS = {
  TRANSPORTER_A: '0a00ad35-df7c-4347-a02e-0b7b378d83e3', // サンプル陸送員A
  TRANSPORTER_B: '90099867-ae35-49f2-9812-fb0d79f817e1', // サンプル陸送員B
  TRANSPORTER_C: 'a998fdf5-4933-4780-8c02-509035a32b93', // サンプル陸送員C
  TRANSPORTER_D: 'c1d2e3f4-a5b6-5c7d-8e9f-01234567890a', // サンプル陸送員D（田中 健太）
  TRANSPORTER_E: 'd2e3f4a5-b6c7-5d8e-9f01-23456789ab0c', // サンプル陸送員E（中村 明美）
} as const;

/**
 * デモ会社ID（sample-rikuso）
 */
const DEMO_COMPANY_ID = 'e32a0499-6456-44ac-9b16-b0001098c259';

/**
 * ベース日付: 2025-10-28（水曜日）
 * スケジュールはこの日付から1週間分を設定
 */
const BASE_DATE = new Date('2025-10-28');

/**
 * 日付を計算する関数
 */
function getDateString(daysOffset: number): string {
  const date = new Date(BASE_DATE);
  date.setDate(date.getDate() + daysOffset);
  return date.toISOString().split('T')[0];
}

/**
 * UUIDを生成する関数（デモ用）
 */
function generateDemoUUID(index: number): string {
  const hex = index.toString().padStart(32, '0');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

/**
 * デモ用ドライバースケジュールデータ
 */
export const DEMO_DRIVER_SCHEDULES: Omit<
  DriverSchedule,
  'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'
>[] = [
  // ===== サンプル陸送員A: 通常の週間スケジュール =====
  // 月曜日（前の週）
  {
    id: generateDemoUUID(1001),
    companyId: DEMO_COMPANY_ID,
    landTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_A,
    scheduleDate: getDateString(-3), // 2025-10-25 (土曜日)
    timeStart: '06:00',
    timeEnd: '20:00',
    status: 'confirmed',
    isBulkCopySource: false,
    notes: '通常勤務',
  },
  // 火曜日
  {
    id: generateDemoUUID(1002),
    companyId: DEMO_COMPANY_ID,
    landTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_A,
    scheduleDate: getDateString(-2), // 2025-10-26 (日曜日)
    timeStart: '06:00',
    timeEnd: '20:00',
    status: 'confirmed',
    isBulkCopySource: false,
    notes: '通常勤務',
  },
  // 水曜日（本日）
  {
    id: generateDemoUUID(1003),
    companyId: DEMO_COMPANY_ID,
    landTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_A,
    scheduleDate: getDateString(0), // 2025-10-28 (水曜日)
    timeStart: '06:00',
    timeEnd: '20:00',
    status: 'registered',
    isBulkCopySource: false,
    notes: '今日の予定',
  },
  // 木曜日
  {
    id: generateDemoUUID(1004),
    companyId: DEMO_COMPANY_ID,
    landTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_A,
    scheduleDate: getDateString(1), // 2025-10-29 (木曜日)
    timeStart: '06:00',
    timeEnd: '20:00',
    status: 'registered',
    isBulkCopySource: false,
    notes: '通常勤務',
  },
  // 金曜日
  {
    id: generateDemoUUID(1005),
    companyId: DEMO_COMPANY_ID,
    landTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_A,
    scheduleDate: getDateString(2), // 2025-10-30 (金曜日)
    timeStart: '06:00',
    timeEnd: '20:00',
    status: 'registered',
    isBulkCopySource: true, // 一括複写元（このスケジュールをテンプレートとして使用）
    notes: '通常勤務・テンプレート用',
  },
  // 土曜日（休み）
  {
    id: generateDemoUUID(1006),
    companyId: DEMO_COMPANY_ID,
    landTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_A,
    scheduleDate: getDateString(3), // 2025-10-31 (土曜日)
    timeStart: '09:00',
    timeEnd: '17:00',
    status: 'registered',
    isBulkCopySource: false,
    notes: '短時間勤務（土曜）',
  },
  // 日曜日（定休日）
  {
    id: generateDemoUUID(1007),
    companyId: DEMO_COMPANY_ID,
    landTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_A,
    scheduleDate: getDateString(4), // 2025-11-01 (日曜日)
    timeStart: '00:00',
    timeEnd: '00:00',
    status: 'registered',
    isBulkCopySource: false,
    notes: '定休日',
  },

  // ===== サンプル陸送員B: 朝出勤・夕方帰宅パターン =====
  {
    id: generateDemoUUID(2001),
    companyId: DEMO_COMPANY_ID,
    landTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_B,
    scheduleDate: getDateString(-2), // 2025-10-26 (日曜日)
    timeStart: '08:00',
    timeEnd: '18:00',
    status: 'confirmed',
    isBulkCopySource: false,
    notes: '標準勤務',
  },
  {
    id: generateDemoUUID(2002),
    companyId: DEMO_COMPANY_ID,
    landTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_B,
    scheduleDate: getDateString(0), // 2025-10-28 (水曜日)
    timeStart: '08:00',
    timeEnd: '18:00',
    status: 'registered',
    isBulkCopySource: false,
    notes: '本日出勤',
  },
  {
    id: generateDemoUUID(2003),
    companyId: DEMO_COMPANY_ID,
    landTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_B,
    scheduleDate: getDateString(1), // 2025-10-29 (木曜日)
    timeStart: '08:00',
    timeEnd: '18:00',
    status: 'registered',
    isBulkCopySource: false,
    notes: '通常勤務',
  },
  {
    id: generateDemoUUID(2004),
    companyId: DEMO_COMPANY_ID,
    landTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_B,
    scheduleDate: getDateString(2), // 2025-10-30 (金曜日)
    timeStart: '08:00',
    timeEnd: '18:00',
    status: 'cancelled', // キャンセルされたスケジュール
    isBulkCopySource: false,
    notes: '予定キャンセル（急病）',
  },

  // ===== サンプル陸送員C: 深夜勤務パターン =====
  {
    id: generateDemoUUID(3001),
    companyId: DEMO_COMPANY_ID,
    landTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_C,
    scheduleDate: getDateString(-2), // 2025-10-26 (日曜日)
    timeStart: '20:00',
    timeEnd: '04:00', // 翌日
    status: 'confirmed',
    isBulkCopySource: false,
    notes: '夜間勤務',
  },
  {
    id: generateDemoUUID(3002),
    companyId: DEMO_COMPANY_ID,
    landTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_C,
    scheduleDate: getDateString(0), // 2025-10-28 (水曜日)
    timeStart: '20:00',
    timeEnd: '04:00', // 翌日
    status: 'registered',
    isBulkCopySource: false,
    notes: '本日深夜勤務',
  },
  {
    id: generateDemoUUID(3003),
    companyId: DEMO_COMPANY_ID,
    landTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_C,
    scheduleDate: getDateString(1), // 2025-10-29 (木曜日)
    timeStart: '20:00',
    timeEnd: '04:00', // 翌日
    status: 'registered',
    isBulkCopySource: true, // 深夜勤務のテンプレート
    notes: '夜間勤務・テンプレート用',
  },

  // ===== サンプル陸送員D: 短時間勤務パターン =====
  {
    id: generateDemoUUID(4001),
    companyId: DEMO_COMPANY_ID,
    landTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_D,
    scheduleDate: getDateString(-2), // 2025-10-26 (日曜日)
    timeStart: '09:00',
    timeEnd: '13:00',
    status: 'confirmed',
    isBulkCopySource: false,
    notes: '午前半日勤務',
  },
  {
    id: generateDemoUUID(4002),
    companyId: DEMO_COMPANY_ID,
    landTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_D,
    scheduleDate: getDateString(0), // 2025-10-28 (水曜日)
    timeStart: '09:00',
    timeEnd: '13:00',
    status: 'registered',
    isBulkCopySource: false,
    notes: '本日午前勤務',
  },
  {
    id: generateDemoUUID(4003),
    companyId: DEMO_COMPANY_ID,
    landTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_D,
    scheduleDate: getDateString(2), // 2025-10-30 (金曜日)
    timeStart: '14:00',
    timeEnd: '18:00',
    status: 'registered',
    isBulkCopySource: false,
    notes: '午後半日勤務',
  },

  // ===== サンプル陸送員E: 未登録スケジュール =====
  {
    id: generateDemoUUID(5001),
    companyId: DEMO_COMPANY_ID,
    landTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_E,
    scheduleDate: getDateString(1), // 2025-10-29 (木曜日)
    timeStart: '06:00',
    timeEnd: '20:00',
    status: 'registered', // まだ登録済みで確認されていない
    isBulkCopySource: false,
    notes: '登録待ち',
  },
  {
    id: generateDemoUUID(5002),
    companyId: DEMO_COMPANY_ID,
    landTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_E,
    scheduleDate: getDateString(2), // 2025-10-30 (金曜日)
    timeStart: '06:00',
    timeEnd: '20:00',
    status: 'registered',
    isBulkCopySource: false,
    notes: '登録待ち',
  },
];

/**
 * デモ用陸送員ID一覧（テストで使用）
 */
export const DEMO_TRANSPORTER_IDS = Object.values(SAMPLE_TRANSPORTERS);

/**
 * デモスケジュール統計
 */
export const DEMO_SCHEDULE_STATS = {
  TOTAL_TRANSPORTERS: Object.keys(SAMPLE_TRANSPORTERS).length,
  TOTAL_SCHEDULES: DEMO_DRIVER_SCHEDULES.length,
  CONFIRMED_SCHEDULES: DEMO_DRIVER_SCHEDULES.filter(s => s.status === 'confirmed').length,
  REGISTERED_SCHEDULES: DEMO_DRIVER_SCHEDULES.filter(s => s.status === 'registered').length,
  CANCELLED_SCHEDULES: DEMO_DRIVER_SCHEDULES.filter(s => s.status === 'cancelled').length,
  BULK_COPY_SOURCE_COUNT: DEMO_DRIVER_SCHEDULES.filter(s => s.isBulkCopySource).length,
};
